// ============================================================================
// EXECUTION ENGINE
// ----------------------------------------------------------------------------
// Calcula a viabilidade prática de um match DEPOIS que a compatibilidade
// principal já foi avaliada. Não decide se os imóveis fazem sentido entre
// si — decide se a operação consegue acontecer na prática.
//
// Camadas consideradas:
//   - torna (valor da diferença entre os imóveis)
//   - direção da diferença (quer receber / pode pagar / aceita ambos)
//   - composição complementar (dinheiro, veículo, outros bens, +1 imóvel)
//   - urgência / timing
//   - aceitação de visitas / prontidão
//   - sinais históricos leves (skips, dismissed)
//
// REGRAS DE NEGÓCIO IMPORTANTES:
//   - Veículo NUNCA é tipo principal — só compõe a diferença.
//   - "Outros bens" idem: só viabiliza torna, nunca substitui imóvel.
//   - Composição complementar só ENTRA se a compatibilidade principal já
//     atingiu um piso mínimo (tratado pelo motor que orquestra).
//   - Quanto mais a operação depender de itens não-monetários, MAIS o
//     execution score cai (operações simples são premiadas).
//   - Baseline neutro (50), nunca otimista.
// ============================================================================

import type { Tables } from "@/integrations/supabase/types";
import {
  readNonCashComposition,
  type NonCashComposition,
  type DifferenceDirection,
} from "@/lib/nonCashComposition";

type Property = Tables<"properties">;
type SwapPref = Tables<"swap_preferences">;

export type DifferenceCoverage =
  | "none"          // diferença ≈ 0 → não precisa de composição
  | "cash_only"     // coberta só com dinheiro (mais simples)
  | "cash_vehicle"  // dinheiro + veículo (média)
  | "cash_multi"    // dinheiro + mais de 1 imóvel
  | "cash_other"    // dinheiro + outro bem
  | "complex"       // várias camadas combinadas
  | "uncovered";    // não há como cobrir com o que foi declarado

export interface ExecutionResult {
  score: number;              // 0-100
  coverage: DifferenceCoverage;
  directionConflict: boolean; // true quando o sentido da torna é incompatível
  /** true quando A quer receber e o troco oferecido fica abaixo do piso declarado. */
  minTornaShortfall?: boolean;
  factors: {
    label: string;
    score: number;
    weight: number;
    note?: string;
  }[];
  notes: string[];            // observações curtas para o explanation builder
}

// Pesos do execution score (somam 1.0)
const EXEC_WEIGHTS = {
  torna: 0.35,         // maior peso: a operação fecha financeiramente?
  direction: 0.20,     // sentido da diferença bate?
  composition: 0.20,   // o quão simples é a composição usada?
  urgency: 0.10,       // urgências compatíveis?
  visits: 0.05,        // disponibilidade prática
  history: 0.10,       // sinais históricos (skip / dismissed)
};

const BASELINE = 50;

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function num(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function readComposition(prefs: SwapPref | null): NonCashComposition {
  return readNonCashComposition((prefs as any)?.non_cash_composition);
}

/**
 * Direção da diferença declarada pelo lado A.
 * Lê primeiro `non_cash_composition.difference_direction` (nova tela);
 * se ausente, infere a partir de `swap_type` legado.
 */
function readDirection(prefs: SwapPref | null): DifferenceDirection | null {
  const comp = readComposition(prefs);
  if (comp.difference_direction) return comp.difference_direction;
  const t = (prefs as any)?.swap_type as string | null | undefined;
  if (t === "menor_mais_dinheiro") return "receive"; // quer ir para imóvel menor → recebe troco
  if (t === "maior_pagando_diferenca") return "pay"; // quer ir para imóvel maior → paga
  if (t === "aberto") return "both";
  return null;
}

// ----------------------------------------------------------------------------
// Sub-scores
// ----------------------------------------------------------------------------

/**
 * 1. TORNA — a diferença monetária está dentro do que A aceita?
 *
 * Regras NOVAS (alinhadas à UI):
 *   - Se A quer RECEBER troco, o usuário declarou um PISO mínimo de dinheiro
 *     (gravado em `min_cash_difference`, ou em `max_cash_difference` quando
 *     o registro é legado e a direção é "receive"). A operação é aderente
 *     somente se a diferença efetiva ≥ piso. Abaixo do piso, o execution
 *     score cai bastante (operação não traz a liquidez que o dono pediu).
 *   - Se A quer PAGAR diferença, `max_cash_difference` é um TETO máximo.
 *     Acima do teto, o score cai.
 *   - Sem direção declarada → comportamento legado (max = teto).
 */
function scoreTorna(
  valueDiff: number,
  refValue: number,
  valA: number,
  valB: number,
  prefsA: SwapPref | null,
): { score: number; note?: string; minShortfall?: boolean } {
  const dir = readDirection(prefsA);
  // Liquidez efetiva que A receberia (negativa = A pagaria)
  const aLiquidity = valA - valB;

  const minDeclared = num((prefsA as any)?.min_cash_difference);
  const maxDeclared = num((prefsA as any)?.max_cash_difference);

  // ── Cenário RECEBER: usuário exige torna mínima (piso) ──────────────────
  // Avaliado ANTES do "valueDiff === 0", pois mesmo em valores iguais o piso
  // não é atendido (A receberia 0, mas exige X).
  if (dir === "receive" || dir === "both") {
    const floor = minDeclared ?? (dir === "receive" ? maxDeclared : null);
    if (floor != null && floor > 0) {
      if (aLiquidity >= floor) {
        return { score: 100, note: "Troco atende ao mínimo declarado" };
      }
      // Liquidez insuficiente (inclui igual ou negativa)
      const effective = Math.max(aLiquidity, 0);
      const shortfall = (floor - effective) / floor;
      if (shortfall <= 0.10) {
        return { score: 55, note: "Troco pouco abaixo do mínimo declarado", minShortfall: true };
      }
      if (shortfall <= 0.30) {
        return { score: 30, note: "Troco bem abaixo do mínimo declarado", minShortfall: true };
      }
      return { score: 10, note: "Troco muito abaixo do mínimo declarado", minShortfall: true };
    }
    // Sem piso declarado → recebe qualquer valor (positivo)
    if (aLiquidity > 0) return { score: 90, note: "Diferença a receber sem piso declarado" };
  }

  if (valueDiff === 0) return { score: 100, note: "Sem diferença de valor" };

  // ── Cenário PAGAR ou neutro: max é teto ────────────────────────────────
  // Em registros legados sem direção, ainda tratamos max_cash_difference como teto.
  const max = maxDeclared;
  if (max != null) {
    if (valueDiff <= max) return { score: 100, note: "Diferença dentro do limite declarado" };
    const overshoot = (valueDiff - max) / Math.max(max, 1);
    if (overshoot <= 0.15) return { score: 55, note: "Diferença pouco acima do limite" };
    if (overshoot <= 0.40) return { score: 30, note: "Diferença bem acima do limite" };
    return { score: 10, note: "Diferença muito acima do limite aceito" };
  }

  // Sem limite explícito → percentual
  const pct = refValue > 0 ? (valueDiff / refValue) * 100 : 100;
  if (pct <= 10) return { score: 90, note: "Diferença pequena (até 10%)" };
  if (pct <= 20) return { score: 75 };
  if (pct <= 35) return { score: 55 };
  if (pct <= 50) return { score: 35 };
  return { score: 20, note: "Diferença grande sem limite declarado" };
}

/**
 * 2. DIREÇÃO DA DIFERENÇA — A quer receber ou pagar? E o cenário pede o quê?
 *  - se A quer SÓ receber e teria que pagar → conflito forte
 *  - se A quer SÓ pagar e teria que receber → conflito forte
 *  - "both" sempre OK
 *  - sem declaração → neutro
 */
function scoreDirection(
  valA: number,
  valB: number,
  prefsA: SwapPref | null,
): { score: number; conflict: boolean; note?: string } {
  const dir = readDirection(prefsA);
  if (!dir) return { score: 60, conflict: false };
  if (dir === "both") return { score: 100, conflict: false, note: "Aceita pagar ou receber" };

  // Cenário do ponto de vista de A: comprar B significa…
  // - valB > valA → A precisaria PAGAR a diferença
  // - valB < valA → A RECEBERIA a diferença
  // - igual → indiferente
  if (valA === valB) return { score: 90, conflict: false };

  const aWouldPay = valB > valA;
  if (dir === "pay" && aWouldPay) return { score: 100, conflict: false, note: "Sentido da torna compatível" };
  if (dir === "receive" && !aWouldPay) return { score: 100, conflict: false, note: "Sentido da torna compatível" };

  // Conflito: o sentido pedido contraria o desejado
  return {
    score: 15,
    conflict: true,
    note: dir === "pay"
      ? "A quer pagar diferença, mas o cenário pede receber"
      : "A quer receber diferença, mas o cenário pede pagar",
  };
}

/**
 * 3. COMPOSIÇÃO — a diferença pode ser fechada com o que A aceita?
 * Avalia QUANTAS camadas extras seriam necessárias.
 * Operação só com dinheiro = simples (alto). Vários itens combinados = complexa.
 */
function scoreComposition(
  valueDiff: number,
  prefsA: SwapPref | null,
): { score: number; coverage: DifferenceCoverage; note?: string } {
  if (valueDiff === 0) return { score: 100, coverage: "none", note: "Não exige composição" };

  const comp = readComposition(prefsA);
  const acceptsCash = (prefsA as any)?.accepts_cash_difference !== false; // default true (legado)

  // Caso 1: aceita só dinheiro como diferença → simples
  if (acceptsCash && !comp.accepts_vehicle && !comp.accepts_multiple_properties && !comp.accepts_other_goods) {
    return { score: 90, coverage: "cash_only", note: "Diferença coberta apenas com dinheiro" };
  }

  // Conta camadas adicionais aceitas (cada uma adiciona complexidade)
  let extraLayers = 0;
  const layerLabels: string[] = [];
  if (comp.accepts_vehicle) { extraLayers++; layerLabels.push("veículo"); }
  if (comp.accepts_multiple_properties) { extraLayers++; layerLabels.push("mais de um imóvel"); }
  if (comp.accepts_other_goods) { extraLayers++; layerLabels.push("outros bens"); }

  // Se A não aceita nada e ainda há diferença → cobertura inviável por A
  if (!acceptsCash && extraLayers === 0) {
    return { score: 15, coverage: "uncovered", note: "Sem meios de cobrir a diferença" };
  }

  // Determina a coverage tag dominante
  let coverage: DifferenceCoverage;
  if (extraLayers === 0) coverage = "cash_only";
  else if (extraLayers === 1 && comp.accepts_vehicle) coverage = "cash_vehicle";
  else if (extraLayers === 1 && comp.accepts_multiple_properties) coverage = "cash_multi";
  else if (extraLayers === 1 && comp.accepts_other_goods) coverage = "cash_other";
  else coverage = "complex";

  // Score: cada camada extra reduz; mas estar aberto a alternativas tem valor
  // (porque viabiliza, mesmo que com mais atrito).
  // 1 camada extra: 70  | 2: 55  | 3: 40
  const score = extraLayers === 0 ? 90 : Math.max(40, 85 - extraLayers * 15);
  const note = layerLabels.length > 0
    ? `Composição usa: ${layerLabels.join(" + ")}`
    : undefined;
  return { score, coverage, note };
}

/**
 * 4. URGÊNCIA / TIMING — alinhamento de prazos.
 */
function scoreUrgency(prefsA: SwapPref | null, prefsB: SwapPref | null): { score: number; note?: string } {
  const compA = readComposition(prefsA);
  const compB = readComposition(prefsB);

  // Lê de non_cash_composition (novo) ou do campo legado urgency
  const uA = compA.urgency || (prefsA as any)?.urgency || null;
  const uB = compB.urgency || (prefsB as any)?.urgency || null;

  if (!uA && !uB) return { score: 60 };
  if (uA && uB) {
    if (uA === uB) return { score: 100, note: `Ambos com urgência ${uA}` };
    // alta vs baixa = atrito; alta vs média ou média vs baixa = ok
    if ((uA === "high" && uB === "low") || (uA === "low" && uB === "high")) {
      return { score: 35, note: "Urgências divergentes" };
    }
    return { score: 70 };
  }
  return { score: 65 };
}

/**
 * 5. VISITAS — disponibilidade operacional.
 */
function scoreVisits(prefsA: SwapPref | null, prefsB: SwapPref | null): number {
  const a = (prefsA as any)?.available_for_visits;
  const b = (prefsB as any)?.available_for_visits;
  if (a === false || b === false) return 30;
  if (a === true && b === true) return 100;
  return 65;
}

/**
 * 6. HISTÓRICO — penaliza candidatos que A já rejeitou (skip / dismissed).
 * `historySignal` deve vir do orquestrador: -N (penalização) a 0.
 */
function scoreHistory(historySignal: number): { score: number; note?: string } {
  if (historySignal === 0) return { score: 70 };
  if (historySignal <= -20) return { score: 20, note: "Você já rejeitou imóveis parecidos antes" };
  if (historySignal <= -10) return { score: 40, note: "Sinal de rejeição anterior" };
  return { score: 55 };
}

// ----------------------------------------------------------------------------
// API principal
// ----------------------------------------------------------------------------

export interface ExecutionInput {
  propertyA: Property;
  prefsA: SwapPref | null;
  propertyB: Property;
  prefsB: SwapPref | null;
  /** Sinal histórico de A→B (skip/dismissed). 0 = neutro, negativo = penaliza. */
  historySignal?: number;
}

export function calculateExecution(input: ExecutionInput): ExecutionResult {
  const { propertyA, prefsA, propertyB, prefsB, historySignal = 0 } = input;

  const valA = Number(propertyA.estimated_value) || 0;
  const valB = Number(propertyB.estimated_value) || 0;
  const valueDiff = Math.abs(valA - valB);

  const torna = scoreTorna(valueDiff, Math.max(valA, valB), valA, valB, prefsA);
  const direction = scoreDirection(valA, valB, prefsA);
  const composition = scoreComposition(valueDiff, prefsA);
  const urgency = scoreUrgency(prefsA, prefsB);
  const visits = scoreVisits(prefsA, prefsB);
  const history = scoreHistory(historySignal);

  const factors = [
    { label: "Torna",         score: torna.score,       weight: EXEC_WEIGHTS.torna * 100,       note: torna.note },
    { label: "Direção",       score: direction.score,   weight: EXEC_WEIGHTS.direction * 100,   note: direction.note },
    { label: "Composição",    score: composition.score, weight: EXEC_WEIGHTS.composition * 100, note: composition.note },
    { label: "Urgência",      score: urgency.score,     weight: EXEC_WEIGHTS.urgency * 100,     note: urgency.note },
    { label: "Visitas",       score: visits,            weight: EXEC_WEIGHTS.visits * 100 },
    { label: "Histórico",     score: history.score,     weight: EXEC_WEIGHTS.history * 100,     note: history.note },
  ];

  // Ponderado puro (sem injetar otimismo)
  const weighted =
    torna.score * EXEC_WEIGHTS.torna +
    direction.score * EXEC_WEIGHTS.direction +
    composition.score * EXEC_WEIGHTS.composition +
    urgency.score * EXEC_WEIGHTS.urgency +
    visits * EXEC_WEIGHTS.visits +
    history.score * EXEC_WEIGHTS.history;

  // Mistura com baseline neutro só para evitar score artificialmente baixo
  // quando vários fatores são "unknown" (todos batem ~60). Sem inflar.
  let score = Math.round(weighted * 0.85 + BASELINE * 0.15);

  // Caps duros
  if (direction.conflict) score = Math.min(score, 30);
  if (composition.coverage === "uncovered") score = Math.min(score, 25);
  if (torna.score <= 15) score = Math.min(score, 30);
  // Hard cap quando a torna mínima exigida não foi atendida com folga
  if (torna.minShortfall) score = Math.min(score, 35);

  score = Math.max(0, Math.min(100, score));

  const notes: string[] = [];
  if (torna.note) notes.push(torna.note);
  if (direction.note) notes.push(direction.note);
  if (composition.note) notes.push(composition.note);
  if (urgency.note) notes.push(urgency.note);
  if (history.note) notes.push(history.note);

  return {
    score,
    coverage: composition.coverage,
    directionConflict: direction.conflict,
    minTornaShortfall: torna.minShortfall === true,
    factors,
    notes,
  };
}

/**
 * Frase descritiva curta sobre a viabilidade — pronta para o explanation
 * builder do compatibility engine.
 */
export function describeExecution(result: ExecutionResult): string {
  if (result.directionConflict) {
    return "O sentido da torna não bate com o que foi declarado.";
  }
  if (result.minTornaShortfall) {
    return "A diferença em dinheiro fica abaixo do mínimo que você declarou querer receber.";
  }
  switch (result.coverage) {
    case "none":
      return "Não exige torna — operação direta.";
    case "cash_only":
      return "A diferença pode ser composta com dinheiro.";
    case "cash_vehicle":
      return "A diferença depende de veículo como parte do pagamento.";
    case "cash_multi":
      return "A operação envolve mais de um imóvel para fechar.";
    case "cash_other":
      return "A diferença usa outro bem como complemento.";
    case "complex":
      return "Operação possível, porém com execução mais complexa.";
    case "uncovered":
      return "Não há meio declarado para cobrir a diferença.";
  }
}